import React from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription'; // Add subscription hook
import { FaCheck, FaCalendarAlt } from 'react-icons/fa';
import './style.css';

const LevelCard = ({ level }) => {
  const { hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription(); // Use subscription context
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get level image URL - use from API or fallback to local
  const getLevelImage = (levelData) => {
    // Use image_url from API if available, otherwise fallback to local mapping
    if (levelData.image_url) {
      return levelData.image_url;
    }
    
    // Fallback mapping for local images based on actual API level codes
    const imageMap = {
      'A0': '/images/levels/PHOTO-N0-2048x2048.jpg',
      'A1': '/images/levels/PHOTO-N1-2048x2048.jpg',
      'A2': '/images/levels/PHOTO-N2-2048x2048.jpg',
      'A3': '/images/levels/PHOTO-N3-2048x2048.jpg',
      'B1': '/images/levels/PHOTO-N4-2048x2048.jpg',
      'B2': '/images/levels/PHOTO-N5-2048x2048.jpg',
      'B3': '/images/levels/PHOTO-N6-2048x2048.jpg'
    };
    
    return imageMap[levelData.level] || '/images/levels/PHOTO-N0-2048x2048.jpg';
  };

  // Get level color - use from API or fallback to default
  const getLevelColor = (levelData) => {
    // If the API provides a color, use it; otherwise use a default
    if (levelData.color) {
      return levelData.color;
    }
    
    // Default color mapping based on level code (minimal hardcoded fallback)
    const defaultColors = {
      'A0': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'A1': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'A2': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'A3': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'B1': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'B2': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'B3': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    };
    
    return defaultColors[levelData.level] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  };

  // Get level display name - use from API or fallback to level code
  const getLevelName = (levelData) => {
    // If the API provides a display name, use it; otherwise use the level code
    return levelData.display_name || levelData.name || levelData.level || 'LEVEL';
  };

  const levelImage = getLevelImage(level);
  const levelColor = getLevelColor(level);
  const levelName = getLevelName(level);

  // Determine button text and path based on subscription status
  const getButtonTextAndPath = () => {
    if (!hasActiveSubscription) {
      // No subscription - redirect to demo activation
      return { text: "Access Demo", path: "/demo-confirmation" };
    } else if (isDemoSubscription) {
      // Demo subscription - show demo access
      return { text: "Access Demo", path: `/levels/${level.level}/lessons` };
    } else if (isPaidSubscription) {
      // Premium subscription - show start learning
      return { text: "Start Learning", path: `/levels/${level.level}/lessons` };
    }
    // Default fallback
    return { text: "Start Learning", path: `/levels/${level.level}/lessons` };
  };

  const { text: buttonText, path: buttonPath } = getButtonTextAndPath();

  return (
    <div className="level-card">
      {/* Level Header with Level and Image */}
      <div className="level-card-header" style={{ background: levelColor }}>
        <div className="level-info">
          <div className="level-cefr-tag">{level.level}</div>
        </div>
        <div className="level-image-container">
          <img 
            src={levelImage} 
            alt={`${level.title} level`}
            className="level-image"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      {/* Level Content */}
      <div className="level-card-content">
        <h3 className="level-card-title">{level.title}</h3>
        
        <div className="level-card-description">
          <p className="level-card-description-text">
            {level.description}
          </p>
          <Link 
            to={`/levels/${level.level}`} 
            className="learn-more-link"
            aria-label={`Learn more about ${level.title}`}
          >
            Learn more →
          </Link>
        </div>

        {/* Level Metrics */}
        <div className="level-metrics">
          <div className="metric-item">
            <span className="metric-number">{level.lessons_count || 0}</span>
            <span className="metric-label">Lessons</span>
          </div>
          <div className="metric-item">
            <span className="metric-number">{level.videos_count || 0}</span>
            <span className="metric-label">Videos</span>
          </div>
          <div className="metric-item">
            <span className="metric-number">{level.exercises_count || 0}</span>
            <span className="metric-label">Exercises</span>
          </div>
        </div>

        <div className="level-separator"></div>

        {/* Level Features */}
        {level.features && level.features.length > 0 && (
          <div className="level-features">
            <h4>Key Features:</h4>
            <ul>
              {level.features.slice(0, 4).map((feature, index) => (
                <li key={index}>
                  <FaCheck />
                  <span>{feature}</span>
                </li>
              ))}
              {level.features.length > 4 && (
                <li className="more-features">
                  <span>+{level.features.length - 4} more features</span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Timestamps */}
        <div className="level-timestamps">
          <div className="timestamp-item">
            <FaCalendarAlt />
            <span>Created: {formatDate(level.created_at)}</span>
          </div>
          {level.updated_at && level.updated_at !== level.created_at && (
            <div className="timestamp-item">
              <FaCalendarAlt />
              <span>Updated: {formatDate(level.updated_at)}</span>
            </div>
          )}
        </div>

        {/* Level Actions */}
        <div className="level-actions">
          <Link 
            to={buttonPath} 
            className="level-start-btn"
            aria-label={`${buttonText} for ${level.title}`}
          >
            {buttonText}
          </Link>
          <Link 
            to={`/levels/${level.level}`} 
            className="level-details-btn"
            aria-label={`View details for ${level.title}`}
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LevelCard;